/**
 * Assembly guide appended to multi-sheet poster PDFs.
 *
 * A poster printed across several sheets is useless without knowing the order
 * the sheets go in and — more importantly — that they must be printed at
 * actual size. A printer's default "fit to page" silently shrinks every sheet
 * by a few millimetres, which is invisible on a single page but makes the
 * seams of a tiled poster refuse to line up.
 *
 * The page is drawn straight onto a 2D canvas at the print sheet's size; it
 * carries no board content, so it needs no surface renderer.
 */

import { mmToPx, paperDimsMm } from "./paper.js";
import type { AssemblyInfo, PaperOrientation, PaperSize } from "./types.js";

const INK = "#16181c";
const MUTED = "#5b6270";
const ACCENT = "#3d7a2a";
const HAIRLINE = "#c8ccd4";
const GLUE_FILL = "#dfe8d8";

export interface AssemblySheetArgs {
  info: AssemblyInfo;
  /** Sheet the guide itself is printed on — same as the poster's sheets. */
  paper: PaperSize;
  orientation: PaperOrientation;
  pixelRatio: number;
}

/**
 * Render the guide and hand back PNG bytes, matching what the page renderers
 * produce so the PDF exporter can embed it the same way.
 */
export async function renderAssemblySheet(args: AssemblySheetArgs): Promise<{
  pngBytes: Uint8Array;
  pixelW: number;
  pixelH: number;
}> {
  const { wMm, hMm } = paperDimsMm(args.paper, args.orientation);
  const pixelW = mmToPx(wMm, args.pixelRatio);
  const pixelH = mmToPx(hMm, args.pixelRatio);

  const canvas = document.createElement("canvas");
  canvas.width = pixelW;
  canvas.height = pixelH;
  try {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Assembly sheet canvas has no 2D context.");

    // Work in millimetres throughout — the layout is a print layout.
    const px = pixelW / wMm;
    const mm = (v: number) => v * px;

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, pixelW, pixelH);

    const margin = 18;
    let y = margin + 6;

    ctx.fillStyle = INK;
    ctx.textBaseline = "alphabetic";
    ctx.font = `600 ${mm(6.2)}px system-ui, -apple-system, Segoe UI, sans-serif`;
    ctx.fillText(
      args.info.title ?? `${args.info.posterLabel}-Poster zusammensetzen`,
      mm(margin),
      mm(y),
    );
    y += 7.5;

    ctx.fillStyle = MUTED;
    ctx.font = `${mm(3.4)}px system-ui, -apple-system, Segoe UI, sans-serif`;
    const sheets = args.info.cols * args.info.rows;
    ctx.fillText(
      `${sheets} Blatt · fertige Größe ${Math.round(args.info.posterWMm)} × ${Math.round(
        args.info.posterHMm,
      )} mm`,
      mm(margin),
      mm(y),
    );
    y += 12;

    const trimMm = args.info.trimMm;
    const steps: Array<[string, string]> = [
      [
        "Ohne Skalierung drucken",
        'Im Druckdialog "Tatsächliche Größe" bzw. 100 % wählen — nicht "An Seite anpassen".' +
          " Sonst passen die Nahtstellen später nicht zusammen.",
      ],
      [
        "An der Schere schneiden",
        `Wo eine gestrichelte Linie mit Scherensymbol steht, den ${trimMm} mm breiten Rand` +
          " abschneiden. Das betrifft nur die linke und obere Kante — und entfernt zugleich" +
          " den weißen Rand, den die meisten Drucker stehen lassen.",
      ],
      ["Nach dem Raster auslegen", "Seite 1 oben links, dann zeilenweise nach rechts."],
      [
        "Auf die Klebeflächen kleben",
        "Die hell hinterlegten Streifen mit dem Klebestift-Symbol sind die Klebeflächen." +
          " Dort das geschnittene Nachbarblatt bündig auflegen, am Bild ausrichten, andrücken." +
          " Die Markierungen verschwinden dabei unter dem oberen Blatt.",
      ],
    ];

    steps.forEach(([title, body], i) => {
      const n = String(i + 1);
      ctx.fillStyle = ACCENT;
      ctx.beginPath();
      ctx.arc(mm(margin + 2.6), mm(y - 1.4), mm(2.6), 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.font = `600 ${mm(3)}px system-ui, -apple-system, Segoe UI, sans-serif`;
      ctx.textAlign = "center";
      ctx.fillText(n, mm(margin + 2.6), mm(y - 0.35));
      ctx.textAlign = "left";

      ctx.fillStyle = INK;
      ctx.font = `600 ${mm(3.8)}px system-ui, -apple-system, Segoe UI, sans-serif`;
      ctx.fillText(title, mm(margin + 8), mm(y));
      y += 5;

      ctx.fillStyle = MUTED;
      ctx.font = `${mm(3.2)}px system-ui, -apple-system, Segoe UI, sans-serif`;
      y = wrapText(ctx, body, mm(margin + 8), mm(y), mm(wMm - margin * 2 - 8), mm(4.4)) / px;
      y += 6;
    });

    drawGrid(ctx, {
      info: args.info,
      x: mm(margin),
      y: mm(y + 2),
      maxW: mm(wMm - margin * 2),
      maxH: mm(hMm - y - margin - 8),
      mm,
    });

    const blob: Blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("toBlob returned null"))),
        "image/png",
      );
    });
    return { pngBytes: new Uint8Array(await blob.arrayBuffer()), pixelW, pixelH };
  } finally {
    // Release the bitmap right away (iPad Safari caps canvas memory), even on failure.
    canvas.width = 0;
    canvas.height = 0;
  }
}

/** Draw the sheet layout with its page numbers. Returns nothing; purely visual. */
function drawGrid(
  ctx: CanvasRenderingContext2D,
  args: {
    info: AssemblyInfo;
    x: number;
    y: number;
    maxW: number;
    maxH: number;
    mm: (v: number) => number;
  },
): void {
  const { cols, rows } = args.info;
  const { mm } = args;
  const gap = mm(1.6);
  // Cell proportions come from the poster itself — its size divided by the
  // grid is exactly one sheet, whichever way round those sheets are fed.
  // Assuming a fixed portrait ratio here would draw a 2×2 A2 poster as four
  // upright sheets when they are in fact landscape.
  // Media posters don't fill the grid, so they carry the real sheet size.
  const { sheetWMm, sheetHMm } = args.info;
  const ratio =
    sheetWMm && sheetHMm
      ? sheetHMm / sheetWMm
      : args.info.posterHMm / rows / (args.info.posterWMm / cols);
  const maxCellW = (args.maxW - gap * (cols - 1)) / cols;
  const maxCellH = (args.maxH - gap * (rows - 1)) / rows;
  let cellW = Math.min(maxCellW, mm(34));
  let cellH = cellW * ratio;
  if (cellH > maxCellH) {
    cellH = maxCellH;
    cellW = cellH / ratio;
  }
  if (cellH <= 0 || cellW <= 0) return;

  ctx.font = `600 ${mm(4)}px system-ui, -apple-system, Segoe UI, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      const x = args.x + c * (cellW + gap);
      const y = args.y + r * (cellH + gap);
      ctx.fillStyle = "#f4f5f7";
      ctx.strokeStyle = HAIRLINE;
      ctx.lineWidth = Math.max(1, mm(0.3));
      ctx.beginPath();
      ctx.rect(x, y, cellW, cellH);
      ctx.fill();
      ctx.stroke();

      // Glue areas first, so the cut lines draw on top of them.
      const strip = mm(2);
      ctx.fillStyle = GLUE_FILL;
      if (c < cols - 1) ctx.fillRect(x + cellW - strip, y, strip, cellH);
      if (r < rows - 1) ctx.fillRect(x, y + cellH - strip, cellW, strip);

      // Cut edges, matching what is printed on the sheets themselves.
      ctx.strokeStyle = ACCENT;
      ctx.setLineDash([mm(1.2), mm(1)]);
      ctx.beginPath();
      if (c > 0) {
        ctx.moveTo(x + strip, y);
        ctx.lineTo(x + strip, y + cellH);
      }
      if (r > 0) {
        ctx.moveTo(x, y + strip);
        ctx.lineTo(x + cellW, y + strip);
      }
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = INK;
      ctx.fillText(String(r * cols + c + 1), x + cellW / 2, y + cellH / 2);
    }
  }

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = MUTED;
  ctx.font = `${mm(3)}px system-ui, -apple-system, Segoe UI, sans-serif`;
  ctx.fillText(
    `Gestrichelt = abschneiden (${args.info.trimMm} mm) · getönt = Klebefläche`,
    args.x,
    args.y + rows * cellH + (rows - 1) * gap + mm(5),
  );
}

/** Naive word wrap. Returns the y position after the last drawn line. */
function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number {
  const words = text.split(" ");
  let line = "";
  let cursor = y;
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (ctx.measureText(candidate).width > maxWidth && line) {
      ctx.fillText(line, x, cursor);
      line = word;
      cursor += lineHeight;
    } else {
      line = candidate;
    }
  }
  if (line) {
    ctx.fillText(line, x, cursor);
  }
  return cursor;
}
